import React, { useState } from 'react';

const CATEGORIES = [
  { value: 'portfolio-blending', label: 'Portfolio Blending' },
  { value: 'risk-metrics', label: 'Risk Metrics' },
  { value: 'monte-carlo', label: 'Monte Carlo Simulation' },
  { value: 'return-distributions', label: 'Return Distributions' },
  { value: 'stress-testing', label: 'Historical Stress Testing (Ex Post)' },
  { value: 'private-beta', label: 'Private Market Beta Model' },
  { value: 'asset-allocation', label: 'Asset Allocation' },
  { value: 'liquidity-costs', label: 'Liquidity & Costs' },
];

const SectionHeading = ({ children }) => (
  <h6 style={{ color: 'var(--primary-color)', fontWeight: 700, marginTop: '1.2rem', marginBottom: '0.5rem' }}>{children}</h6>
);

const Formula = ({ children }) => (
  <code style={{ display: 'block', background: '#f8f9fa', padding: '8px 12px', borderRadius: 4, margin: '8px 0', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{children}</code>
);

const PortfolioBlendingSection = () => (
  <div>
    <SectionHeading>Weighted Portfolio Construction</SectionHeading>
    <p>When multiple portfolios are combined, each portfolio's contribution is determined by its user-specified weight. All weights must sum to 100%.</p>
    <p>The blended portfolio's expected return is the weighted average of the constituent portfolio returns:</p>
    <Formula>R_p = Σ(w_i × R_i)</Formula>
    <p>where <code>w_i</code> is the weight allocated to portfolio <em>i</em> and <code>R_i</code> is that portfolio's expected return.</p>

    <SectionHeading>Portfolio Volatility</SectionHeading>
    <p>Blended volatility accounts for correlations between portfolios using the full covariance matrix:</p>
    <Formula>σ_p = √(w^T × Cov × w)</Formula>
    <p>The covariance matrix is constructed from the correlation matrix and individual portfolio volatilities:</p>
    <Formula>Cov[i,j] = ρ[i,j] × σ_i × σ_j</Formula>
    <p>where <code>ρ[i,j]</code> is the correlation between portfolios <em>i</em> and <em>j</em>. Correlations are sourced from the capital market assumptions stored in the database.</p>

    <SectionHeading>Asset Class Lookthrough</SectionHeading>
    <p>The blended lookthrough is the weighted sum of each portfolio's underlying asset class exposures. This provides a view of the true asset class composition of the combined portfolio, regardless of the number of constituent portfolios.</p>
  </div>
);

const RiskMetricsSection = () => (
  <div>
    <SectionHeading>Return/Risk Ratio (Sharpe-like)</SectionHeading>
    <p>Measures expected return per unit of total volatility:</p>
    <Formula>Return/Risk Ratio = R_p / σ_p</Formula>

    <SectionHeading>Sortino Ratio</SectionHeading>
    <p>Similar to the Sharpe ratio but only penalises downside volatility, making it more relevant for investors concerned with losses:</p>
    <Formula>Sortino = (R_p - R_f) / σ_downside</Formula>
    <Formula>σ_downside = √(mean(min(0, r - r_f)²))</Formula>
    <p>where <code>R_f</code> is the risk-free rate and the downside deviation is calculated only from returns below the risk-free rate across all simulated years.</p>

    <SectionHeading>Calmar Ratio</SectionHeading>
    <p>Measures return relative to the average worst peak-to-trough decline:</p>
    <Formula>Calmar = R_p / |avg_max_drawdown|</Formula>
    <p>The average maximum drawdown is computed across all Monte Carlo simulation paths.</p>

    <SectionHeading>Value at Risk (VaR) — 95%</SectionHeading>
    <p>The 5th percentile of simulated one-year returns. In 95% of simulated outcomes, the portfolio return exceeds this value.</p>
    <Formula>VaR(95%) = 5th percentile of simulated 1-year returns</Formula>

    <SectionHeading>Conditional Value at Risk (CVaR) — 95%</SectionHeading>
    <p>The expected (average) return in the worst 5% of one-year outcomes. This captures tail risk more comprehensively than VaR alone:</p>
    <Formula>CVaR(95%) = mean of returns ≤ VaR(95%)</Formula>

    <SectionHeading>Maximum Drawdown</SectionHeading>
    <p>The largest peak-to-trough decline in portfolio value within a simulation path. Both the average and worst-case maximum drawdowns are reported across all Monte Carlo paths.</p>

    <SectionHeading>Win Rate</SectionHeading>
    <p>The percentage of simulated years that produced a positive return.</p>

    <SectionHeading>SRM Risk Band</SectionHeading>
    <p>The Standard Risk Measure (SRM) assigns a risk band from 1 to 7 based on the estimated number of negative annual returns in a 20-year period:</p>
    <div className="table-responsive mt-2 mb-2">
      <table className="table table-sm table-bordered" style={{ fontSize: '0.85rem', maxWidth: 500 }}>
        <thead>
          <tr style={{ backgroundColor: 'var(--primary-color)', color: '#fff' }}>
            <th style={{ padding: '6px 12px' }}>Risk Band</th>
            <th style={{ padding: '6px 12px' }}>Label</th>
            <th style={{ padding: '6px 12px' }}>Negative Years in 20</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>1</td><td>Very Low</td><td>Less than 0.5</td></tr>
          <tr><td>2</td><td>Low</td><td>0.5 to less than 1</td></tr>
          <tr><td>3</td><td>Low to Medium</td><td>1 to less than 2</td></tr>
          <tr><td>4</td><td>Medium</td><td>2 to less than 3</td></tr>
          <tr><td>5</td><td>Medium to High</td><td>3 to less than 4</td></tr>
          <tr><td>6</td><td>High</td><td>4 to less than 6</td></tr>
          <tr><td>7</td><td>Very High</td><td>6 or greater</td></tr>
        </tbody>
      </table>
    </div>
    <p>The estimated negative years figure is derived from the Monte Carlo simulation results.</p>
  </div>
);

const MonteCarloSection = () => (
  <div>
    <SectionHeading>Geometric Brownian Motion (GBM)</SectionHeading>
    <p>The Monte Carlo engine generates 1,000 independent simulation paths over a 30-year horizon using a Geometric Brownian Motion model. Each annual step is modelled as:</p>
    <Formula>ln(S_t / S_(t-1)) = (μ - 0.5σ²)Δt + σ√(Δt) × Z</Formula>
    <p>where:</p>
    <ul>
      <li><code>μ</code> = portfolio expected return (ex ante)</li>
      <li><code>σ</code> = portfolio expected volatility</li>
      <li><code>Δt</code> = 1 year</li>
      <li><code>Z</code> = standard normal random variable (drawn independently for each path and year)</li>
    </ul>
    <p>The drift term <code>(μ - 0.5σ²)</code> adjusts for the difference between arithmetic and geometric returns, ensuring the median simulated path converges to the expected compound growth rate.</p>

    <SectionHeading>Percentile Paths</SectionHeading>
    <p>At each year across all 1,000 paths, the portfolio values are sorted to extract the 5th, 25th, 50th (median), 75th and 95th percentile outcomes. These are displayed as the fan chart bands on the projection chart.</p>

    <SectionHeading>Success Probability</SectionHeading>
    <p>For portfolios with a defined return objective (e.g. CPI+3%), the engine calculates the proportion of simulation paths where terminal wealth at the portfolio's time horizon exceeds the target. This is reported as the "Probability of Meeting Objective".</p>
  </div>
);

const ReturnDistributionsSection = () => (
  <div>
    <SectionHeading>Normal Distribution Approximation</SectionHeading>
    <p>The box plot chart uses the portfolio's expected return and volatility to approximate the distribution of annual returns assuming normality. Percentiles shown are:</p>
    <ul>
      <li><strong>5th percentile</strong> — worst-case scenario (1-in-20 year outcome)</li>
      <li><strong>25th percentile</strong> — below-average year</li>
      <li><strong>50th percentile (median)</strong> — typical year</li>
      <li><strong>75th percentile</strong> — above-average year</li>
      <li><strong>95th percentile</strong> — best-case scenario</li>
    </ul>

    <SectionHeading>Returns Histogram</SectionHeading>
    <p>The annualised returns distribution histogram is generated from 5,000 Monte Carlo simulations. For each holding period (1, 3, 5, 7, 10 years), the engine simulates return paths and computes the annualised compound return:</p>
    <Formula>R_annualised = (S_T / S_0)^(1/T) - 1</Formula>
    <p>The histogram bins these annualised returns to show the dispersion and central tendency. Longer holding periods typically produce a narrower distribution, illustrating the effect of time diversification.</p>
  </div>
);

const StressTestingSection = () => (
  <div>
    <SectionHeading>Ex Post Stress Testing</SectionHeading>
    <p>Historical stress testing examines how the blended portfolio would have performed during specific market events using actual monthly returns data.</p>

    <SectionHeading>Event Windows</SectionHeading>
    <p>Each stress event is defined by a start date and end date (e.g. the GFC spans from November 2007 to February 2009). The system retrieves monthly returns for all relevant asset classes across the event window.</p>

    <SectionHeading>Cumulative Return Calculation</SectionHeading>
    <p>For each month in the event window, the blended portfolio return is computed as the weighted sum of asset class returns:</p>
    <Formula>R_event = Σ_t(Σ_ac(w_ac × r_ac,t))</Formula>
    <p>where <code>w_ac</code> is the lookthrough weight to each asset class and <code>r_ac,t</code> is the monthly return for that asset class in month <em>t</em>. Cumulative returns are compounded across the event window.</p>

    <SectionHeading>Listed Market Proxy Returns</SectionHeading>
    <p>Where monthly return data has gaps for a listed asset class, the system fills missing values using a proxy index based on the asset class's risk profile:</p>
    <ul>
      <li><strong>Defensive asset classes</strong> (bonds, credit, cash) — gaps are filled using the US aggregate bond index (BND_US)</li>
      <li><strong>Growth asset classes</strong> (equities, real estate, infrastructure, etc.) — gaps are filled using the global equity index (EQ_WORLD)</li>
    </ul>
    <p>This ensures complete return coverage across all stress event windows even where an asset class's index data begins later than the event start date.</p>

    <SectionHeading>Private Market Proxy Returns</SectionHeading>
    <p>Private market asset classes (private equity, private debt, real assets) do not have monthly market-observable returns. To include them in stress testing, monthly proxy returns are synthesised using a 3-factor beta model fitted against publicly traded indices (see the <em>Private Market Beta Model</em> section for details). Where quarterly actual returns exist, those are used directly; the beta model fills the remaining months. This ensures full coverage across all stress event windows.</p>

    <SectionHeading>Asset Class Contribution Attribution</SectionHeading>
    <p>The contribution of each asset class to the total event return is calculated as the asset class weight multiplied by its cumulative return over the event period. This allows users to see which exposures drove losses or provided protection during historical crises.</p>
  </div>
);

const PrivateBetaSection = () => (
  <div>
    <SectionHeading>The Challenge</SectionHeading>
    <p>Private market assets (private equity, private debt, real assets) do not have daily or monthly market-observable returns. To include them in stress testing and return distribution analysis, a proxy return series must be synthesised.</p>

    <SectionHeading>3-Factor OLS Regression</SectionHeading>
    <p>Where quarterly actual returns are available for a private market asset class, a 3-factor ordinary least squares (OLS) regression is fitted against publicly traded proxies:</p>
    <Formula>r_private(t) = β₁ × BND_US(t) + β₂ × EQ_WORLD(t) + β₃ × CREDIT_GL(t)</Formula>
    <p>The three factors are:</p>
    <ul>
      <li><strong>BND_US</strong> — US aggregate bond index (interest rate sensitivity)</li>
      <li><strong>EQ_WORLD</strong> — Global equity index (equity beta)</li>
      <li><strong>CREDIT_GL</strong> — Global credit index (credit spread sensitivity)</li>
    </ul>
    <p>The intercept is excluded from the synthesis to avoid double-counting alpha assumptions already embedded in the expected return.</p>

    <SectionHeading>Quarterly Actuals vs Beta Backcast</SectionHeading>
    <p>Where quarterly actual returns exist for a private market asset class, those returns are used directly. The beta model is used only to fill months where no actual data is available (backcast). This hybrid approach maximises the use of real data while extending the history far enough to cover all stress event windows.</p>
  </div>
);

const AssetAllocationSection = () => (
  <div>
    <SectionHeading>Lookthrough Methodology</SectionHeading>
    <p>Each portfolio in the blender has a defined allocation to underlying asset classes, stored in the portfolio registry. The lookthrough aggregates these allocations weighted by the user's portfolio mix.</p>

    <SectionHeading>Broad Asset Classes</SectionHeading>
    <p>The system uses 11 broad asset classes:</p>
    <ol>
      <li>Australian Equities</li>
      <li>International Equities</li>
      <li>Real Estate</li>
      <li>Infrastructure</li>
      <li>Global Alpha</li>
      <li>Cash</li>
      <li>Government Bonds</li>
      <li>Corporate Debt</li>
      <li>Private Debt</li>
      <li>Private Equity</li>
      <li>Real Assets (Private Market)</li>
    </ol>

    <SectionHeading>Growth / Defence Classification</SectionHeading>
    <p>Asset classes are classified into two groups for reporting:</p>
    <ul>
      <li><strong>Growth:</strong> Australian Equities, International Equities, Real Estate, Infrastructure, Global Alpha, Private Equity, Real Assets (Private Market)</li>
      <li><strong>Defence:</strong> Cash, Government Bonds, Corporate Debt, Private Debt</li>
    </ul>
    <p>Growth and defence totals are displayed in the lookthrough table to give a quick sense of the portfolio's risk posture.</p>
  </div>
);

const LiquidityCostsSection = () => (
  <div>
    <SectionHeading>Management Fees &amp; Costs</SectionHeading>
    <p>The blended figure is the weighted average of each constituent portfolio's management fees and costs (per ASIC RG 97: investment management fee plus indirect costs, net of rebates), sourced from the PDS fee data of each SAA's canonical registry portfolio. Asset-class building blocks use their configured indirect cost ratio.</p>

    <SectionHeading>Liquidity Profile</SectionHeading>
    <p>Each portfolio has a liquidity profile categorised into three tiers:</p>
    <ul>
      <li><strong>Daily liquid</strong> — assets redeemable within one business day (e.g. listed equities, cash, government bonds)</li>
      <li><strong>Monthly liquid</strong> — assets redeemable within one month (e.g. some managed funds, corporate debt)</li>
      <li><strong>Quarterly liquid</strong> — assets redeemable within one quarter (e.g. private market funds with quarterly redemption windows)</li>
    </ul>
    <p>The blended liquidity profile is the weighted average across constituent portfolios, giving users visibility into the overall liquidity characteristics of their combined allocation.</p>

    <SectionHeading>Income Yield</SectionHeading>
    <p>Where available, the blended income yield is calculated as the weighted average of each portfolio's estimated income yield. This represents the expected regular income (distributions, dividends, coupons) as a percentage of total portfolio value. The income rate of each sub-asset class is set on the investment-config Sub Asset Classes tab; classes ticked "Income = Return" are treated as income-only, with income equal to the default CMA scenario's expected total return.</p>
  </div>
);

const SECTION_COMPONENTS = {
  'portfolio-blending': PortfolioBlendingSection,
  'risk-metrics': RiskMetricsSection,
  'monte-carlo': MonteCarloSection,
  'return-distributions': ReturnDistributionsSection,
  'stress-testing': StressTestingSection,
  'private-beta': PrivateBetaSection,
  'asset-allocation': AssetAllocationSection,
  'liquidity-costs': LiquidityCostsSection,
};

const BlenderMethodologyModal = ({ show, onClose }) => {
  const [selectedCategory, setSelectedCategory] = useState(CATEGORIES[0].value);

  if (!show) return null;

  const SectionComponent = SECTION_COMPONENTS[selectedCategory];

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header" style={{ backgroundColor: 'var(--primary-color)', color: '#fff' }}>
            <h5 className="modal-title">
              <i className="bi bi-journal-text me-2"></i>Portfolio Blender Methodology
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <div className="modal-body" style={{ minHeight: 400 }}>
            <div className="mb-3">
              <select
                className="form-select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={{ maxWidth: 400, borderColor: 'var(--input-border)' }}
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div style={{ fontSize: '0.9rem', lineHeight: 1.6 }}>
              {SectionComponent && <SectionComponent />}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlenderMethodologyModal;
